
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled ? 'bg-white/90 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="https://public.readdy.ai/ai/img_res/686cbf1a-1f0d-4489-8e90-6c4f87ffc05b.png" 
                alt="XELEC S.R.L. Logo" 
                className="h-8 w-auto mr-3"
              />
              <span className={`text-xl font-bold transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}>XELEC S.R.L.</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              {['Home', 'VPN Service', 'About Us', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase().replace(/\s+/g, '-'))}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'
                  }`}
                >
                  {item}
                </button>
              ))}
              <button
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-teal-400 to-blue-500 text-white px-6 py-2 rounded-full text-sm font-medium hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <img 
          src="https://public.readdy.ai/ai/img_res/865199957bd81399f75b32876c82db24.jpg"
          alt="Network Security Background"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-40"
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-8 w-full">
          <div className="max-w-3xl">
            <div className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8">
              <span className="text-teal-300 text-sm font-medium tracking-wide uppercase">SECURE VPN SERVICE</span>
            </div>
            
            <h1 className="text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
              Private.<br />
              Secure.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-400">Unlimited.</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
              Experience true internet freedom with military-grade encryption and lightning-fast connection speeds.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-teal-400 to-blue-500 text-white px-8 py-4 rounded-lg text-lg font-medium hover:shadow-xl transition-all duration-300 flex items-center justify-center whitespace-nowrap cursor-pointer"
              >
                Start Free Trial
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <button 
                onClick={() => scrollToSection('vpn-service')}
                className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-white hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Service Features */}
      <section id="vpn-service" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-col lg:flex-row items-start justify-between mb-16">
            <div className="lg:w-1/2">
              <span className="text-teal-500 text-sm font-semibold tracking-wider uppercase mb-4 block">WHY CHOOSE US</span>
              <h2 className="text-5xl font-bold text-slate-900 mb-6">
                Enterprise-Grade <em className="font-serif">Security</em>
              </h2>
            </div>
            <div className="lg:w-1/2 lg:pl-12">
              <p className="text-lg text-gray-600 leading-relaxed">
                Our VPN service provides uncompromising security and privacy protection for individuals and businesses. 
                With cutting-edge encryption technology and a global network infrastructure, we ensure your digital 
                communications remain private and secure.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Military Encryption Card */}
            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-slate-800 to-slate-600 rounded-3xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">256-Bit Encryption</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Military-grade AES-256 encryption protects your data with the same security standards used by 
                government agencies and financial institutions worldwide.
              </p>
              <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">
                BANK-LEVEL SECURITY
              </span>
            </div>

            {/* Global Network Card */}
            <div className="bg-slate-900 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300">
              <div className="w-20 h-20 flex items-center justify-center mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Minimalist%20world%20map%20visualization%20with%20glowing%20connection%20points%20and%20network%20lines%20in%20cyan%20blue%20color%2C%20simple%20geometric%20design%20showing%20global%20server%20locations%20for%20VPN%20service%2C%20clean%20tech%20illustration%20with%20transparent%20background%20and%20modern%20style&width=80&height=80&seq=network-map-001&orientation=squarish"
                  alt="Global Network"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Global Network</h3>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Access our extensive network of optimized servers across multiple continents, ensuring fast 
                and reliable connections wherever you are in the world.
              </p>
              <span className="inline-block bg-lime-400 text-slate-900 text-xs font-semibold px-3 py-1 rounded-full">
                50+ COUNTRIES
              </span>
            </div>

            {/* Zero Logs Card */}
            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-blue-500 rounded-3xl flex items-center justify-center mb-6">
                <i className="ri-file-forbid-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Zero Logs Policy</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                We maintain a strict no-logs policy, ensuring that your browsing activity, connection times, 
                and personal data are never stored or monitored.
              </p>
              <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">
                PRIVACY GUARANTEED
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* About Us */}
      <section id="about-us" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center mb-6">
                <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
                <span className="text-teal-500 text-sm font-semibold tracking-wider uppercase">ABOUT XELEC S.R.L.</span>
              </div>
              
              <h2 className="text-5xl font-bold text-slate-900 mb-8 leading-tight">
                Protecting Your Digital <em className="font-serif text-teal-600">Freedom</em> Since Day One
              </h2>
              
              <div className="space-y-6 mb-12">
                <p className="text-gray-600 text-lg leading-relaxed">
                  XELEC S.R.L. was founded with a simple mission: to provide individuals and businesses with 
                  the tools they need to maintain their privacy and security in an increasingly connected world. 
                  Based in San Rafael, Argentina, we specialize in developing cutting-edge VPN technology.
                </p>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Our commitment to privacy, security, and user experience drives everything we do. We believe 
                  that internet freedom is a fundamental right, and we work tirelessly to ensure our users can 
                  browse, communicate, and work online without compromising their personal information.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-8 items-start">
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-500 mb-1">99.9%</div>
                  <div className="text-sm text-gray-500">Uptime</div>
                </div>
                <div className="hidden sm:block w-px h-12 bg-gray-300"></div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-500 mb-1">24/7</div>
                  <div className="text-sm text-gray-500">Support</div>
                </div>
                <div className="hidden sm:block w-px h-12 bg-gray-300"></div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-500 mb-1">No Limits</div>
                  <div className="text-sm text-gray-500">Bandwidth</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%203D%20illustration%20showing%20interconnected%20security%20nodes%20and%20encrypted%20data%20streams%20in%20cyan%20blue%20and%20purple%20gradients%2C%20modern%20cybersecurity%20concept%20with%20geometric%20shapes%20and%20flowing%20network%20connections%2C%20clean%20minimalist%20design%20representing%20VPN%20technology%20and%20data%20protection&width=600&height=500&seq=about-visual-001&orientation=landscape"
                  alt="Security Technology"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-8">
          <div className="bg-slate-900 rounded-2xl p-12 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 to-blue-500/10"></div>
            
            <div className="relative z-10 grid lg:grid-cols-2 gap-12">
              <div>
                <span className="text-teal-400 text-sm font-semibold tracking-wider uppercase mb-4 block">GET IN TOUCH</span>
                <h2 className="text-4xl font-bold text-white mb-6">
                  Ready to Secure Your Connection?
                </h2>
                <p className="text-gray-400 text-lg leading-relaxed">
                  Have questions? Our team is here to help you get started with XELEC VPN and ensure 
                  your digital privacy is protected.
                </p>
              </div>
              
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-5 h-5 flex items-center justify-center mt-1">
                    <i className="ri-mail-line text-teal-400"></i>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Email</div>
                    <div className="text-white text-lg font-medium">contact@xelecsrl.com</div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-5 h-5 flex items-center justify-center mt-1">
                    <i className="ri-phone-line text-teal-400"></i>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Phone</div>
                    <div className="text-white text-lg font-medium">+1 6016027828</div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-5 h-5 flex items-center justify-center mt-1">
                    <i className="ri-map-pin-line text-teal-400"></i>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Address</div>
                    <div className="text-white text-lg font-medium leading-relaxed">
                      Avenida Bartolomé Mitre 1175<br />
                      San Rafael, M5600GJL, Argentina
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Policy */}
      <section id="privacy-policy" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-8">
          <h2 className="text-4xl font-bold text-slate-900 mb-12 text-center">Privacy Policy</h2>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="prose prose-lg max-w-none">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">Information Collection</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                At XELEC S.R.L., we are committed to protecting your privacy. We do not collect, store, or log 
                your browsing activity, connection times, or IP addresses when using our VPN service. Our 
                no-logs policy ensures that your online activities remain completely private.
              </p>
              
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">Data Security</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                All data transmitted through our VPN service is encrypted using military-grade AES-256 encryption. 
                We implement industry-standard security measures to protect any minimal account information 
                (email address for account creation) that we may collect.
              </p>
              
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">Third-Party Services</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We do not share, sell, or distribute any personal information to third parties. Our VPN service 
                is designed to enhance your privacy, not compromise it. We may use anonymous analytics to 
                improve our service performance, but this data cannot be linked to individual users.
              </p>
              
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">Contact Information</h3>
              <p className="text-gray-600 leading-relaxed">
                If you have any questions about this Privacy Policy or our practices, please contact us at 
                contact@xelecsrl.com or visit our office at Avenida Bartolomé Mitre 1175, San Rafael, 
                M5600GJL, Argentina.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start mb-16">
            <div className="flex items-center mb-8 lg:mb-0">
              <img 
                src="https://public.readdy.ai/ai/img_res/686cbf1a-1f0d-4489-8e90-6c4f87ffc05b.png" 
                alt="XELEC S.R.L. Logo" 
                className="h-8 w-auto mr-3"
              />
              <span className="text-white text-2xl font-bold">XELEC S.R.L.</span>
            </div>
            <p className="text-gray-400 italic">Your trusted partner in digital privacy</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div>
              <h4 className="text-white font-semibold mb-4">
                <a href="#vpn-service" className="hover:text-teal-400 transition-colors">Product</a>
              </h4>
              <ul className="space-y-3">
                <li><a href="#vpn-service" className="text-gray-400 hover:text-white transition-colors">VPN Features</a></li>
                <li><a href="#vpn-service" className="text-gray-400 hover:text-white transition-colors">Security</a></li>
                <li><a href="#vpn-service" className="text-gray-400 hover:text-white transition-colors">Servers</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-white transition-colors">Pricing</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">
                <a href="#about-us" className="hover:text-teal-400 transition-colors">Company</a>
              </h4>
              <ul className="space-y-3">
                <li><a href="#about-us" className="text-gray-400 hover:text-white transition-colors">About Us</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-white transition-colors">Contact</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-white transition-colors">Careers</a></li>
                <li><a href="#about-us" className="text-gray-400 hover:text-white transition-colors">Press Kit</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">
                <a href="#contact" className="hover:text-teal-400 transition-colors">Resources</a>
              </h4>
              <ul className="space-y-3">
                <li><a href="#contact" className="text-gray-400 hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#vpn-service" className="text-gray-400 hover:text-white transition-colors">Setup Guides</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-white transition-colors">FAQ</a></li>
                <li><a href="#about-us" className="text-gray-400 hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            
            <div className="text-right">
              <div className="text-white text-lg font-serif mb-2">© 2025 XELEC S.R.L.</div>
              <div className="text-gray-400 text-sm mb-8">All rights reserved</div>
              <div className="flex justify-end space-x-4">
                <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors">
                  <i className="ri-linkedin-line text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors">
                  <i className="ri-twitter-line text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors">
                  <i className="ri-facebook-line text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col lg:flex-row items-center justify-between pt-8 border-t border-gray-700">
            <div className="lg:w-1/3 mb-8 lg:mb-0">
              <img 
                src="https://readdy.ai/api/search-image?query=Abstract%20VPN%20tunnel%20visualization%20with%20glowing%20data%20streams%20and%20secure%20connection%20pathways%20in%20cyan%20and%20blue%20colors%2C%20modern%20tech%20illustration%20showing%20encrypted%20network%20flow%20with%20geometric%20elements%20and%20privacy%20concept&width=280&height=180&seq=tunnel-viz-001&orientation=landscape"
                alt="VPN Security Tunnel"
                className="w-70 h-45 object-cover object-top rounded-3xl"
              />
            </div>
            
            <div className="lg:w-1/3 text-center mb-8 lg:mb-0">
              <h3 className="text-white text-3xl font-serif mb-4">
                Start protecting your<br />privacy today
              </h3>
            </div>
            
            <div className="lg:w-1/3 lg:pl-8">
              <p className="text-gray-400 text-sm mb-4">
                Stay updated with security tips<br />and product updates
              </p>
              <div className="flex">
                <input 
                  type="email" 
                  placeholder="Enter your email"
                  className="flex-1 bg-transparent border border-gray-600 rounded-l-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400"
                />
                <button className="bg-gradient-to-r from-teal-400 to-blue-500 text-white px-6 py-3 rounded-r-xl hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Subscribe <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <a 
              href="https://readdy.ai/?origin=logo" 
              className="text-gray-500 text-sm hover:text-teal-400 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
